import * as React from "react";

// Third Party
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHead,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import FileHandleViewer from "@/components/FileHandleViewer";

// S3_BUCKET_LINK
// moment
import moment from "moment";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { ArrowLeft, PencilLine } from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";
import { RequireEditDocs } from "@/services/require-edit-docs";

import { useToast } from "@/components/ui/use-toast";
// Services and Utils
import { getDivDetails } from "@/services/tax-obligations";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";

export const Route = createFileRoute(
  "/_layout-private/report/tax-obligations/dividend-plan/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  // Clean up the divId from localStorage
  const divId = localStorage.getItem("divId")?.replace(/^"(.+)"$/, "$1");
  const { delegationPlan } = useAccessPermission();
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: divDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["dividend-details", divId],
    queryFn: () => getDivDetails(divId as string),
    enabled: !!divId,
    // Add retry and stale time options
    retry: 1,
    staleTime: 5000,
  });

  const daysFromNow = calculateDaysFromNow(divDetails?.data?.updatedAt);

  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dividend-details", divId],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!divDetails?.data.id) return;
    requireEditMutation.mutate({
      editId: divDetails?.data.id,
      editRequestType: "DIVIDEND_PLAN_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  if (isLoading) return <DinamixSkeletonTable />;
  if (error) return <div>Error loading dividend details</div>;
  if (!divDetails) return <div>No dividend details found</div>;
  return (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link
                replace
                to="/report/tax-obligations"
                search={{ active: "dividendPlan", startYear: 0, endYear: 0 }}
              >
                ລາຍງານແຜນມອບພັນທະອາກອນ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍລະອຽດແຜນມອບເງິນປັນຜົນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/tax-obligations",
                search: { active: "dividendPlan", startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-1 mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <CardTitle>
            ລາຍລະອຽດແຜນການມອບເງິນປັນຜົນ ປີ {divDetails?.data.yearly}
          </CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>
          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">

            {daysFromNow <= 3 ? delegationPlan.isUpdate && (
              <Button
                onClick={() => {
                  navigate({
                    to: "/report/tax-obligations/dividend-plan/edit",
                  });
                }}
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )
              :
              delegationPlan.isUpdate && (
                divDetails?.editRequestStatus === "REQUEST" ? (
                  <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
                ) : (
                  <Button
                    onClick={() => {
                      setOpen(true);
                    }}
                    variant="secondary"
                    className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
                  >
                    <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                  </Button>
                ))}
            <ModalRequireEditDoc
              isOpen={open}
              onClose={() => setOpen(false)}
              onConfirm={handleRequireEdit}
              isLoading={requireEditMutation.isPending}
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table className="border">
            <TableHeader>
              <TableRow className="bg-gray-100 [&>*]:border [&>*]:font-bold [&>*]:text-black">
                <TableHead>ລາຍການ ປີ{divDetails?.data.yearly}</TableHead>
                <TableHead>ລາຍລະອຽດ</TableHead>
                <TableHead>ວັນທີສ້າງແຜນ</TableHead>
                <TableHead>ເອກະສານຕິດຄັດ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="[&>*]:border">
                <TableCell className="font-medium">ແຜນເງິນປັນຜົນ</TableCell>
                <TableCell>
                  {divDetails?.data.plannedDividend?.toLocaleString()} ກີບ
                </TableCell>
                <TableCell>
                  {moment(divDetails?.data.createdAt).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="text-blue-500 underline">
                  {divDetails?.data.docFile
                    ? delegationPlan.isDownload && (
                      <FileHandleViewer
                        fileUrl={divDetails?.data.docFile}
                        showBtnPreview={true}
                        text="ເບິ່ງເອກະສານ"
                      />
                    )
                    : "-"}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">ຈຳນວນງວດທັງໝົດ</TableCell>
                <TableCell>{divDetails?.data.totalPhase} ງວດ</TableCell>
                <TableCell colSpan={2}> </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}
